'use client';

import { useEffect, useState } from 'react';

import { If } from '@kit/ui/if';

import { useCaptcha } from '../captcha/client';
import { usePasswordSignUpFlow } from '../hooks/use-sign-up-flow';
import { isUserAlreadyRegisteredError } from '../utils/is-expected-auth-failure';
import { AuthErrorAlert } from './auth-error-alert';
import { ExistingAccountHint } from './existing-account-hint';
import { PasswordSignUpForm } from './password-sign-up-form';
import { VerifyEmailPanel } from './verify-email-panel';

interface EmailPasswordSignUpContainerProps {
  displayTermsCheckbox?: boolean;
  defaultValues?: {
    email: string;
  };
  onSignUp?: (userId?: string) => unknown;
  emailRedirectTo: string;
  captchaSiteKey?: string;

  /**
   * Fired when the flow enters (and leaves) the "check your email" state, so
   * the surrounding page can drop the sign-up chrome — heading, OAuth buttons
   * — that no longer applies once the account exists.
   */
  onVerificationChange?: (verifying: boolean) => void;
}

export function EmailPasswordSignUpContainer({
  defaultValues,
  onSignUp,
  emailRedirectTo,
  displayTermsCheckbox,
  captchaSiteKey,
  onVerificationChange,
}: EmailPasswordSignUpContainerProps) {
  const captcha = useCaptcha({ siteKey: captchaSiteKey });
  const captchaLoading = !captcha.isReady;

  // Captured on submit: the flow hook only reports that a confirmation was
  // sent, never where, and the panel has to be able to name the address.
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const {
    signUp: onSignupRequested,
    loading,
    error,
    showVerifyEmailAlert,
  } = usePasswordSignUpFlow({
    emailRedirectTo,
    onSignUp,
    captchaToken: captcha.token,
    resetCaptchaToken: captcha.reset,
  });

  // `!loading` matters on the second attempt: after "use a different email"
  // the previous mutation is still reported as successful, so without it a
  // resubmit would flash the confirmation panel for the new address before
  // that address had actually been sent anything.
  const verifying = showVerifyEmailAlert && !loading && submittedEmail !== null;

  useEffect(() => {
    onVerificationChange?.(verifying);
  }, [verifying, onVerificationChange]);

  return (
    <>
      <If condition={verifying}>
        <VerifyEmailPanel
          email={submittedEmail ?? ''}
          emailRedirectTo={emailRedirectTo}
          onChangeEmail={() => setSubmittedEmail(null)}
        />
      </If>

      <If condition={!verifying}>
        <AuthErrorAlert error={error} />

        <If condition={isUserAlreadyRegisteredError(error)}>
          <ExistingAccountHint className={'my-4'} />
        </If>

        <div>
          <PasswordSignUpForm
            onSubmit={(params) => {
              setSubmittedEmail(params.email);

              return onSignupRequested(params);
            }}
            loading={loading}
            defaultValues={defaultValues}
            displayTermsCheckbox={displayTermsCheckbox}
            captchaLoading={captchaLoading}
          />

          {captcha.field}
        </div>
      </If>
    </>
  );
}
